import { useRouteParams } from '@vueuse/router';
import { fetchGameFiltersHandler } from '~/handlers/fetch-game-filters.handler';
import { fetchGameFilterOptionsHandler } from '~/handlers/fetch-game-filter-options.handler';

export const useCasino = () => {
  const { currentRouteName } = useRoutes();
  const { t } = useTexts();
  const { nomenclatureForCasinoGames } = useFeatureFlagsNew();

  const casinoNomenclature = computed<string>(() => {
    if (nomenclatureForCasinoGames.value === 'ilottery') {
      return t('general.ilottery');
    }

    if (nomenclatureForCasinoGames.value === 'games') {
      return t('general.games');
    }

    return t('general.casino');
  });

  const categoryParam = useRouteParams<string>('category', '', {
    transform: normalizeRouteParams,
  });

  const isCasinoCategoryPage = computed<boolean>(() => {
    return currentRouteName.value === 'games-category-category';
  });

  function isCasinoCategoryPageActive(category: string): boolean {
    return isCasinoCategoryPage.value && categoryParam.value === category;
  }

  const gameParam = useRouteParams<string>('game', '', {
    transform: normalizeRouteParams,
  });

  const isCasinoGamePage = computed<boolean>(() => {
    return currentRouteName.value === 'games-provider-game';
  });

  function isCasinoGamePageActive(game: string): boolean {
    return isCasinoGamePage.value && gameParam.value === game;
  }

  const isProvidersHomePage = computed<boolean>(() => {
    return currentRouteName.value === 'games-providers';
  });

  async function getGames(filterParams: any) {
    try {
      const { ApiService } = useApiService();

      const params = new URLSearchParams(filterParams).toString();
      const url = '/casino-games/filter?' + params;
      const response = await ApiService.get(url);
      const gamesData = response.data;

      return gamesData;
    } catch (error) {
      console.error('Erro ao buscar jogos:', error);
      throw error;
    }
  }

  /**
   * Nova função que usa a API do core (apiapostatudo) para buscar jogos com filtros
   * Esta função substitui a antiga getGames e deve ser usada para todas as novas implementações
   */
  async function getGamesFromCore(
    filterParams: any,
    options?: { silent?: boolean },
  ) {
    try {
      // Converter os parâmetros do formato antigo para o novo formato
      const params: GameFiltersParams = {};

      // Extrair categorias do array de filterParams
      const categories: string[] = [];
      const providers: string[] = [];
      const types: string[] = [];
      let term = '';
      let sort = '';
      let sort_by = '';
      let sort_dir = '';
      let page = 1;
      let per_page = 24;

      filterParams.forEach(([key, value]: [string, any]) => {
        if (key === 'categories[]') {
          categories.push(value);
        } else if (key === 'providers[]') {
          providers.push(value);
        } else if (key === 'types[]') {
          types.push(value);
        } else if (key === 'term') {
          term = value;
        } else if (key === 'sort') {
          sort = value;
        } else if (key === 'sort_by') {
          sort_by = value;
        } else if (key === 'sort_dir') {
          sort_dir = value;
        } else if (key === 'page') {
          page = Number(value) || 1;
        } else if (key === 'per_page') {
          per_page = Number(value) || 24;
        }
      });

      if (categories.length > 0) {
        params.categories = categories;
      }

      if (providers.length > 0) {
        params.providers = providers;
      }

      if (term) {
        params.term = term;
      }

      if (sort) {
        params.sort = sort;
      }

      if (types.length > 0) {
        params.types = types;
      }

      if (sort_by) {
        params.sort_by = sort_by;
      }

      if (sort_dir) {
        params.sort_dir = sort_dir;
      }

      params.page = page;
      params.per_page = per_page;

      // Chamar o handler que usa a nova API
      const { filterData } = await fetchGameFiltersHandler(params);

      if (!filterData) {
        throw new Error('Nenhum dado retornado da API');
      }

      // Converter o formato da resposta para o formato esperado pelo código existente
      return {
        data: filterData.data.map((game: FilteredGame) => ({
          id: game.id,
          slug: game.slug,
          name: game.name,
          image: game.image,
          provider: game.provider,
          game_type: game.type,
          demo: game.demo,
          pay_today: game.pay_today ?? '0',
          ribbon: game.ribbon ?? null,
          tags: game.tags ?? null,
        })),
        current_page: filterData.current_page,
        from: filterData.from,
        to: filterData.to,
        total: filterData.total,
        last_page: filterData.last_page,
        per_page: filterData.per_page,
        categories: filterData.categories.map((cat: GameFilterCategory) => ({
          name: cat.name,
          slug: cat.slug,
        })),
        providers: filterData.providers.map((prov: GameFilterProvider) => ({
          name: prov.name,
          slug: prov.slug,
          image: prov.image,
        })),
      };
    } catch (error) {
      if (!options?.silent) {
        console.error('[useCasino] Erro ao buscar jogos da API core:', error);
      }
      throw error;
    }
  }

  function normalizeThumbnail(
    thumbnailPath: string,
    preserveQueryParams = false,
  ): string {
    if (!thumbnailPath) {
      return '';
    }

    // Remove /public, /mobile ou /ipad que podem vir duplicados do backend
    let normalized = thumbnailPath
      .replace('/public', '')
      .replace('/mobile', '')
      .replace('/ipad', '');

    // Remove query parameters apenas se preserveQueryParams for false
    if (!preserveQueryParams) {
      const parts = normalized.split('?');

      normalized = parts[0] || '';
    }

    return normalized;
  }

  function getThumbnailPathSet(
    thumbnail: string,
    isProviderType = false,
    shouldNormalizeThumbnail = true,
  ): string {
    if (isProviderType || !shouldNormalizeThumbnail) {
      return thumbnail || '';
    }

    if (!thumbnail || typeof thumbnail !== 'string') {
      return '';
    }

    const normalized = normalizeThumbnail(thumbnail);

    // Validate that we have a valid URL path after normalization
    if (!normalized || normalized.length < 2) {
      return '';
    }

    return `${normalized}/mobile 200w, ${normalized}/ipad 600w, ${normalized}/public 1200w`;
  }

  /**
   * Busca as opções de filtros disponíveis (categorias, providers, tipos)
   * Esta função usa a rota /api/games/filter-options do core
   */
  async function getGameFilterOptions(): Promise<GameFilterOptionsResponse | null> {
    try {
      const { filterOptions } = await fetchGameFilterOptionsHandler();

      if (!filterOptions) {
        console.warn('[useCasino] Nenhuma opção de filtro retornada da API');

        return null;
      }

      return filterOptions;
    } catch (error) {
      console.error('[useCasino] Erro ao buscar opções de filtros:', error);

      return null;
    }
  }

  return {
    casinoNomenclature,
    categoryParam,
    isCasinoCategoryPage,
    isCasinoCategoryPageActive,
    gameParam,
    isCasinoGamePage,
    isCasinoGamePageActive,
    isProvidersHomePage,
    getGames,
    getGamesFromCore,
    getGameFilterOptions,
    normalizeThumbnail,
    getThumbnailPathSet,
  };
};
